import React, { useEffect, useState } from 'react';
import styles from './TabbedLists.module.scss';
import classNames from 'classnames';
import i18n from '../../i18n';
import PropTypes from 'prop-types';
import GuideList from '../GuideList';
import MyListEvents from '../MyListEvents';
import ObjectList from '../ObjectList';

const TabbedLists = ({
    defaultTab = 0,
    tabs = [],
    items = {
        objects: [],
        events: [],
        guides: [],
    },
    showRemoveButtons = false,
    isMyList = false,
}) => {
    const [currentTab, setActive] = useState(defaultTab);

    tabs = tabs.filter(
        (tab) => items[tab.listName] && items[tab.listName].count > 0
    );

    useEffect(() => {
        setActive(0);
    }, [items]);

    return (
        <div className={styles['TabbedLists']}>
            <div className={styles['TabbedLists__GrayBorder']}>
                <nav
                    aria-label={i18n.t('Nav.tabs')}
                    className={styles['TabbedLists__Nav']}>
                    {tabs && tabs.length > 0 && (
                        <ul className={styles['TabbedLists__List']}>
                            {tabs.map((tab, i) => {
                                if (
                                    !!items[tab.listName] &&
                                    !items[tab.listName].count
                                ) {
                                    return null;
                                }

                                return (
                                    <li
                                        className={styles['TabbedLists__Item']}
                                        key={'Tab' + i}>
                                        <button
                                            className={classNames(
                                                styles['TabbedLists__ItemLink'],
                                                {
                                                    [styles[
                                                        'TabbedLists__ItemLink--Active'
                                                    ]]: i === currentTab,
                                                }
                                            )}
                                            aria-label={
                                                tab?.label ||
                                                i18n.t(
                                                    `TabbedList.labels.${tab.listName}`
                                                )
                                            }
                                            aria-controls={'Tab' + i}
                                            onClick={() => setActive(i)}>
                                            <span
                                                className={
                                                    styles[
                                                        'TabbedLists__ItemLabel'
                                                    ]
                                                }>
                                                {tab?.label ||
                                                    i18n.t(
                                                        `TabbedList.labels.${tab.listName}`
                                                    )}
                                            </span>
                                            {i == currentTab && (
                                                <span className="sr-only">
                                                    ({i18n.t('generic.current')}
                                                    )
                                                </span>
                                            )}
                                            <span
                                                className={
                                                    styles[
                                                        'TabbedLists__Item--Hover'
                                                    ]
                                                }></span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </nav>
            </div>
            <div className={styles['TabbedLists__Wrapper']}>
                {tabs &&
                    tabs.length > 0 &&
                    tabs.map((tab, i) => (
                        <Tab
                            key={i}
                            listName={tab.listName}
                            tabId={i}
                            currentTab={currentTab}
                            items={items}
                            showRemoveButtons={showRemoveButtons}
                            isMyList={isMyList}
                        />
                    ))}
            </div>
        </div>
    );
};

const Tab = ({
    listName,
    currentTab,
    tabId,
    items,
    showRemoveButtons,
    isMyList,
}) => {
    if (currentTab !== tabId) return null;

    const { objects, eventOccurrences, guides } = items;

    return (
        <div
            id={'Tab' + tabId}
            className={classNames(styles['TabbedLists__Content'], {
                [styles['TabbedLists__Content--Active']]: currentTab === tabId,
            })}>
            <div className={styles['TabbedLists__ContentContainer']}>
                {(() => {
                    switch (listName) {
                        case 'guides':
                            return (
                                <GuideList
                                    items={guides.results}
                                    showRemoveButtons={showRemoveButtons}
                                    isMyList={isMyList}
                                />
                            );

                        case 'eventOccurrences':
                            return (
                                <div
                                    className={
                                        styles['TabbedLists__ListContainer']
                                    }>
                                    <MyListEvents
                                        items={eventOccurrences.results}
                                        showRemoveButtons={showRemoveButtons}
                                        isMyList={isMyList}
                                    />
                                </div>
                            );

                        case 'objects':
                            return (
                                <div
                                    className={
                                        styles['TabbedLists__ListContainer']
                                    }>
                                    <ObjectList
                                        items={objects.results}
                                        showRemoveButtons={showRemoveButtons}
                                        isMyList={isMyList}
                                    />
                                </div>
                            );
                        default:
                            return null;
                    }
                })()}
            </div>
        </div>
    );
};

Tab.propTypes = {
    label: PropTypes.string,
    currentTab: PropTypes.number,
    tabId: PropTypes.number,
    guides: PropTypes.array,
    objects: PropTypes.array,
    events: PropTypes.array,
    isMyList: PropTypes.bool,
};

TabbedLists.propTypes = {
    tabs: PropTypes.array,
    defaultTab: PropTypes.number,
    items: PropTypes.object,
    isMyList: PropTypes.bool,
};

export default TabbedLists;
